/* Stackable filters on any column of a list, for somebody who knows the data.
 *
 * The three book screens each shipped with two or three fixed controls. That is
 * the right first screen and it is not a tool: a rep who wants California
 * multifamily over 200 units with nobody in HubSpot had no way to ask for it,
 * and the fields to ask with were already in the view.
 *
 * So the fixed controls stay exactly where they are, and this adds a second
 * layer underneath them. James's rule on 2026-09-15: core fields first,
 * advanced fields hidden until somebody needs them. Nothing here is on screen
 * until a person opens it, and anything they set stays visible after they
 * close it, because a filter you cannot see is a list that lies about itself.
 *
 * PostgREST already accepts a filter on any column, so this needs no new view
 * and no new endpoint. What was missing was a way to say it.
 */

export type FieldType = "text" | "number" | "date" | "boolean" | "enum";

export type FieldSpec = {
  /** The column, exactly as the api view names it. */
  key: string;
  /** What a rep calls it. "Portfolio size", not "property_count". */
  label: string;
  type: FieldType;
  /** For an enum, every value the column holds. */
  options?: readonly string[];
  /** One short line, only where the column would otherwise mislead. */
  hint?: string;
};

export type Operator =
  | "eq" | "neq" | "gt" | "gte" | "lt" | "lte"
  | "contains" | "starts" | "empty" | "present" | "in";

export type Rule = { field: string; op: Operator; value: string };

/* What each operator is called on screen. The words are the reader's, not
   SQL's: "is at least" beats "gte" for somebody between calls. */
export const OPERATOR_LABEL: Record<Operator, string> = {
  eq: "is",
  neq: "is not",
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  contains: "contains",
  starts: "starts with",
  empty: "is empty",
  present: "is not empty",
  in: "is one of",
};

/* Which operators make sense for which kind of column. A "starts with" on a
   unit count is noise in a menu somebody is reading between calls. */
export const OPERATORS_FOR: Record<FieldType, readonly Operator[]> = {
  text: ["contains", "eq", "neq", "starts", "empty", "present"],
  number: ["gte", "lte", "eq", "neq", "gt", "lt", "empty", "present"],
  date: ["gte", "lte", "eq", "empty", "present"],
  boolean: ["eq"],
  enum: ["eq", "neq", "in", "empty", "present"],
};

/** An operator that needs no value, so the box is hidden rather than ignored. */
export const NEEDS_NO_VALUE: readonly Operator[] = ["empty", "present"];

/* ---------------------------------------------------------------------------
 * The catalogue.
 *
 * Written by hand rather than generated, because the labels are the whole
 * point: a menu reading unit_count, hq_state and newest_source_date is the
 * database's vocabulary, and the person reading it sells EV charging. The keys
 * and types are checked against the generated src/api/types.ts by
 * scripts/check_filter_fields.py, so a column that is renamed or dropped in the
 * lake fails the build here rather than returning a 400 at a rep.
 *
 * Enum options are the values the live lake held on 2026-09-15. A value that
 * appears later is still filterable, because the enum row falls back to a text
 * box when the value is not in the list.
 * ------------------------------------------------------------------------ */

export const FIELDS: Record<string, readonly FieldSpec[]> = {
  company: [
    { key: "name", label: "Name", type: "text" },
    { key: "hq_city", label: "City", type: "text" },
    { key: "hq_state", label: "State", type: "text", hint: "Two-letter code." },
    { key: "website", label: "Website", type: "text" },
    { key: "property_count", label: "Properties we hold", type: "number" },
    { key: "people_count", label: "People we hold", type: "number" },
    {
      key: "relevance", label: "What type of firm", type: "enum",
      options: ["target", "service_provider", "lender"],
    },
    {
      key: "kind", label: "Firm or single property", type: "enum",
      options: ["operating_company", "single_property", "unclear"],
    },
    {
      key: "hubspot_status", label: "In HubSpot", type: "enum",
      options: ["yes", "no"],
      hint: "Based on one sync date, not a check per company.",
    },
    { key: "do_not_contact", label: "Do not contact", type: "boolean" },
    { key: "source_count", label: "How many sources", type: "number" },
    { key: "newest_source_date", label: "Newest source", type: "date" },
    { key: "created_at", label: "Date added", type: "date" },
    { key: "updated_at", label: "Last changed", type: "date" },
  ],
  person: [
    { key: "full_name", label: "Name", type: "text" },
    { key: "title", label: "Job title", type: "text" },
    { key: "employer_name", label: "Employer", type: "text" },
    {
      key: "level", label: "Seniority", type: "enum",
      options: ["c_suite", "vp", "director", "regional_manager", "manager",
                "contributor", "unknown"],
    },
    {
      key: "job_function", label: "What they do", type: "enum",
      options: ["development", "construction", "asset_management",
                "property_management", "operations", "sales", "unknown"],
    },
    {
      key: "standing", label: "Still in the job", type: "enum",
      options: ["current", "unknown"],
    },
    { key: "has_mobile", label: "Has a mobile", type: "boolean" },
    { key: "has_email", label: "Has an email", type: "boolean" },
    { key: "has_phone", label: "Has a phone", type: "boolean" },
    { key: "has_linkedin", label: "Has LinkedIn", type: "boolean" },
    {
      key: "hubspot_status", label: "In HubSpot", type: "enum",
      options: ["yes", "no"],
    },
    { key: "source_count", label: "How many sources", type: "number" },
    { key: "newest_source_date", label: "Newest source", type: "date" },
    { key: "last_confirmed_at", label: "Job last confirmed", type: "date" },
  ],
  property: [
    { key: "name", label: "Property name", type: "text" },
    { key: "address_line1", label: "Street", type: "text" },
    { key: "city", label: "City", type: "text" },
    { key: "county", label: "County", type: "text" },
    { key: "state", label: "State", type: "text", hint: "Two-letter code." },
    { key: "zip", label: "ZIP", type: "text" },
    {
      key: "property_type", label: "Type", type: "enum",
      options: ["multifamily", "commercial", "mixed_use", "other", "unknown"],
    },
    {
      key: "unit_count", label: "Units", type: "number",
      hint: "Excludes properties with no unit count.",
    },
    { key: "parking_spaces", label: "Parking spaces", type: "number" },
    { key: "affordable_flag", label: "Affordable", type: "boolean" },
    {
      key: "has_existing_ev_chargers", label: "Existing chargers", type: "boolean",
    },
    { key: "owner_company_name", label: "Owner", type: "text" },
    { key: "developer_company_name", label: "Developer", type: "text" },
    { key: "jurisdiction_name", label: "Jurisdiction", type: "text" },
    { key: "stakeholder_count", label: "People on it", type: "number" },
    { key: "project_count", label: "Projects on it", type: "number" },
    { key: "source_count", label: "How many sources", type: "number" },
    { key: "newest_source_date", label: "Newest source", type: "date" },
  ],
  criteria_set: [
    { key: "name", label: "Name", type: "text" },
    { key: "label", label: "Label", type: "text" },
    { key: "description", label: "Description", type: "text" },
    { key: "created_by", label: "Created by", type: "text" },
    { key: "run_count", label: "Times run", type: "number" },
    { key: "last_run_at", label: "Last run", type: "date" },
    { key: "created_at", label: "Created", type: "date" },
  ],
  job: [
    {
      key: "kind", label: "What", type: "enum",
      options: ["campaign_run", "lead_sheet", "crm_export", "orum_export", "enrichment_sweep",
                "file_import", "apollo_fill", "apollo_phone",
                "review_merge", "review_undo", "process_capture",
                "campaign_enrichment", "apollo_campaign", "thin_export", "notice", "campaign_measure"],
    },
    {
      key: "status", label: "Status", type: "enum",
      options: ["queued", "running", "done", "failed", "cancelled"],
    },
    { key: "requested_by", label: "Asked by", type: "text" },
    { key: "error", label: "Error", type: "text" },
    { key: "attempts", label: "Attempts", type: "number" },
    { key: "queued_at", label: "Queued", type: "date" },
    { key: "finished_at", label: "Finished", type: "date" },
  ],
};

export function fieldsFor(view: string): readonly FieldSpec[] {
  return FIELDS[view] ?? [];
}

export function specFor(view: string, key: string): FieldSpec | undefined {
  return fieldsFor(view).find((f) => f.key === key);
}

/* ---------------------------------------------------------------------------
 * The URL.
 *
 * One parameter holding every rule, because view state goes in the URL and a
 * power user's whole point is sending somebody the list they are looking at.
 * Rules are separated by "~" and their three parts by ":". Only the value is
 * escaped, so a rule stays readable in the address bar: adv=state:eq:CA~
 * unit_count:gte:200
 * ------------------------------------------------------------------------ */

export function encodeRules(rules: readonly Rule[]): string {
  return rules
    .filter((r) => r.field && r.op)
    .map((r) => `${r.field}:${r.op}:${encodeURIComponent(r.value ?? "")}`)
    .join("~");
}

export function decodeRules(raw: string | null, view: string): Rule[] {
  if (!raw) return [];
  const out: Rule[] = [];
  for (const part of raw.split("~")) {
    if (!part) continue;
    const bits = part.split(":");
    const field = bits[0] ?? "";
    const op = bits[1] ?? "";
    const rest = bits.slice(2);
    // A rule naming a column this view does not have is dropped rather than
    // sent. PostgREST would answer 400 and the screen would read as broken,
    // when the real cause is a pasted link from another screen.
    if (!specFor(view, field)) continue;
    if (!(op in OPERATOR_LABEL)) continue;
    let value = "";
    try {
      value = decodeURIComponent(rest.join(":"));
    } catch {
      // A hand-edited URL can hold a % that is not an escape. Keep the rule
      // with the raw text rather than dropping a filter silently.
      value = rest.join(":");
    }
    out.push({ field, op: op as Operator, value });
  }
  return out;
}

/** A rule with nothing in its value does not narrow anything, so it is not sent. */
export function isComplete(rule: Rule): boolean {
  if (NEEDS_NO_VALUE.includes(rule.op)) return true;
  return rule.value.trim() !== "";
}

/** What the chip says, in words: "Units is at least 200". */
export function describe(view: string, rule: Rule): string {
  const spec = specFor(view, rule.field);
  const label = spec?.label ?? rule.field;
  const op = OPERATOR_LABEL[rule.op];
  if (NEEDS_NO_VALUE.includes(rule.op)) return `${label} ${op}`;
  if (spec?.type === "boolean") return `${label} ${rule.value === "true" ? "yes" : "no"}`;
  return `${label} ${op} ${rule.value}`;
}

/* ---------------------------------------------------------------------------
 * Applying them.
 *
 * postgrest-js has a typed method per operator, used here rather than the
 * generic .filter(), so the library escapes the value. A comma in a search for
 * "Jones, Lang" would otherwise be read as a list separator.
 * ------------------------------------------------------------------------ */

type Queryable = {
  eq: (c: string, v: unknown) => Queryable;
  neq: (c: string, v: unknown) => Queryable;
  gt: (c: string, v: unknown) => Queryable;
  gte: (c: string, v: unknown) => Queryable;
  lt: (c: string, v: unknown) => Queryable;
  lte: (c: string, v: unknown) => Queryable;
  ilike: (c: string, v: string) => Queryable;
  is: (c: string, v: null | boolean) => Queryable;
  not: (c: string, op: string, v: unknown) => Queryable;
  in: (c: string, v: readonly unknown[]) => Queryable;
};

export function applyRules<Q>(q: Q, view: string, rules: readonly Rule[]): Q {
  let out = q as unknown as Queryable;
  for (const rule of rules) {
    if (!isComplete(rule)) continue;
    const spec = specFor(view, rule.field);
    if (!spec) continue;
    const v = rule.value.trim();
    const num = spec.type === "number" ? Number(v) : NaN;
    // A field given something its column cannot hold reaches PostgREST as a
    // cast error, and the screen then has to explain a failure that should
    // never have been sent. The date guard was added on 2026-09-15 after
    // ?adv=created_at:gte:not-a-date produced a 400 and an empty list.
    if (spec.type === "number" && !NEEDS_NO_VALUE.includes(rule.op) && Number.isNaN(num)) continue;
    if (spec.type === "date" && !NEEDS_NO_VALUE.includes(rule.op)
        && Number.isNaN(new Date(v).getTime())) continue;
    const value: unknown = spec.type === "number" ? num
      : spec.type === "boolean" ? v === "true"
      : v;

    switch (rule.op) {
      case "eq": out = out.eq(rule.field, value); break;
      case "neq": out = out.neq(rule.field, value); break;
      case "gt": out = out.gt(rule.field, value); break;
      case "gte": out = out.gte(rule.field, value); break;
      case "lt": out = out.lt(rule.field, value); break;
      case "lte": out = out.lte(rule.field, value); break;
      case "contains": out = out.ilike(rule.field, `%${v}%`); break;
      case "starts": out = out.ilike(rule.field, `${v}%`); break;
      case "empty": out = out.is(rule.field, null); break;
      case "present": out = out.not(rule.field, "is", null); break;
      case "in":
        out = out.in(rule.field, v.split(",").map((s) => s.trim()).filter(Boolean));
        break;
    }
  }
  return out as unknown as Q;
}
